import { useEffect } from 'react'
import type { RefObject } from 'react'
import type { ChartManager } from '../core/ChartManager'
import type { ViewportSyncGroup, ViewportSyncData } from '../types/chart'
import {
  deriveSessionDateKeys,
  resolveAnchorVisibleRange,
  windowFromRange,
} from '../utils/anchorWindow'

/**
 * Subscribe the chart to a viewport (date-range) sync group. Local scroll /
 * zoom is published as `{from, to}` timestamps; remote events apply via
 * `setVisibleDateRange`.
 *
 * On an ANCHORED group (`group.anchored`, the History grid) an anchored chart
 * also publishes the range as a window relative to its own anchor, and an
 * anchored receiver replays that window on its own day, so cells that sit on
 * different event days scroll together by time of day. Charts without an
 * anchor, and every plain group, keep the absolute-range behaviour unchanged.
 *
 * Echo prevention: `isSyncing` flag short-circuits the local publisher while
 * we're applying a remote update — without it, kline's OnVisibleRangeChange
 * would fire on our own setVisibleDateRange call and loop.
 */
export function useViewportSync(
  managerRef: RefObject<ChartManager | null>,
  viewportSync: ViewportSyncGroup | undefined,
  chartId: string | undefined,
): void {
  useEffect(() => {
    const manager = managerRef.current
    if (!manager || !viewportSync || !chartId) return

    const isSyncing = { current: false }
    const anchored = viewportSync.anchored === true

    const onLocalViewport = (data: ViewportSyncData) => {
      if (isSyncing.current) return
      viewportSync.publish(chartId, anchored ? withWindow(manager, data) : data)
    }
    manager.subscribeViewport(onLocalViewport)

    viewportSync.subscribe(chartId, (data) => {
      isSyncing.current = true
      manager.executeViewport(anchored ? onOwnDay(manager, data) : data)
      // Release the guard on next frame so kline's OnVisibleRangeChange
      // fired from setVisibleDateRange settles first; otherwise echo
      // suppression can miss a late-arriving event.
      requestAnimationFrame(() => {
        isSyncing.current = false
      })
    })

    return () => {
      manager.unsubscribeViewport(onLocalViewport)
      viewportSync.unsubscribe(chartId)
    }
  }, [managerRef, viewportSync, chartId])
}

/** Attach the anchor-relative window when this chart has an anchor. */
export function withWindow(manager: ChartManager, data: ViewportSyncData): ViewportSyncData {
  const anchor = manager.getDataLayer().getAnchorTimestamp()
  if (anchor == null) return data
  return { ...data, window: windowFromRange(anchor, data.from, data.to, manager.getLoadedBars()) }
}

/**
 * Replay a received window on this chart's own anchor day. Without an anchor
 * (or without a window) the absolute range applies as before. A window that
 * holds no loaded bars here falls back to the event day, the same data-aware
 * rule the Visible hours control uses.
 */
export function onOwnDay(manager: ChartManager, data: ViewportSyncData): ViewportSyncData {
  if (!data.window) return data
  const anchor = manager.getDataLayer().getAnchorTimestamp()
  if (anchor == null) return data
  const bars = manager.getLoadedBars()
  const win = data.window
  const hasOffset = (win.startDayOffset ?? 0) !== 0 || (win.endDayOffset ?? 0) !== 0
  const sessionDates = hasOffset ? deriveSessionDateKeys(bars) : undefined
  const { from, to } = resolveAnchorVisibleRange(bars, anchor, win, sessionDates)
  return { from, to }
}
